import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Card, Collapse, Form, Container, Button } from 'react-bootstrap';
import { MultiSelectAsync } from './MultiSelectAsync';
import FeatureNav from './FeatureNav';
import FooterSection from './FooterSection';
import HeaderComponent from './HeaderComponent';
import BreadCrumbs from './BreadCrumbs';
import Loader from './Loader';
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';
import { Tabulator, PageModule, DownloadModule, ExportModule, SortModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import * as d3 from "d3";

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule]);

interface VendorSearch {
  fiscal_year: number;
  entity_name: string;
  vendor_name: string;
  vendor_beg_name_id: number;
  net_amount: number;
  ids: number;
} 

const EntityVendorPaymentSearch = () => {
  const [showHeaderInfo, setShowHeaderInfo] = useState(false);
  const [selectedVendorSearch, setSelectedVendorSearch] = useState<string | null>(null);
  const [masterData, setMasterData] = useState<VendorSearch[] | null>(null);
  const [selectedFiscalYears, setSelectedFiscalYears] = useState<number[] | null>(null);
  const [selectedEntityNames, setSelectedEntityNames] = useState<string[] | null>(null);
  const [selectedVendorNames, setSelectedVendorNames] = useState<string[] | null>(null);
  const [allFiscalYears, setAllFiscalYears] = useState<number[] | null>(null);
  const [allEntityNames, setAllEntityNames] = useState<string[] | null>(null);
  const [allVendorNames, setAllVendorNames] = useState<string[] |null>(null);
  const [filteredData, setFilteredData] = useState<VendorSearch[] | null>(null);
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
  const chartRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
    const formattedVendorSearch = selectedVendorSearch?.toLowerCase()?.replaceAll(" ", " ") || ""
    if (!tabulatorRef.current) return;
    tabulatorRef.current.download(type, `${formattedVendorSearch}_vendor_payment_search_results.${type}`);
  };

  const handleD3ChartDownload = (svgNode: SVGSVGElement) => {
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgNode);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      context?.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);

      const pngUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = 'chart.png';
      a.click();
    };
    img.src = url;
  };

  // Fetch Vendor Name Search data on mount
  useEffect(() => {
    if (!selectedVendorSearch) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchVendorSearchData = async () => {
      setLoading(true);
      try {
        const payload = { name: selectedVendorSearch };
        const vendorSearchNameEncodedParam = encodeURIComponent(JSON.stringify(payload));
        const vendorPaymentsNameSearchString = `/?function=getVendorSearch&parameter=${vendorSearchNameEncodedParam}`;

        const buildVendorNameApiUrl = (apiEndpointUrl: string = apiEndpoint, searchParameter: string = vendorPaymentsNameSearchString) => {
          return `${apiEndpointUrl}${searchParameter}`;
        }

        const res = await fetch(buildVendorNameApiUrl(), { signal });
        const vendorSearchData = res.ok ? await res.json() : [];
        
        setMasterData(vendorSearchData);
      } catch (error: any) {
        if (error.name !== 'AbortError') console.error("Fetch failed:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendorSearchData();
    return () => controller.abort();
  }, [selectedVendorSearch]);

  // Reset filters on new vendor search
  useEffect(() => {
    setSelectedFiscalYears(null);
    setSelectedEntityNames(null);
    setSelectedVendorNames(null);
  },[masterData]);

  // Initialize Tabulator Table and implement filters
  useEffect(() => {
    if (!tableRef.current || !masterData) return;

    // Add Filters
    let tableData = [...masterData];
    if (selectedFiscalYears?.length) {
      tableData = tableData.filter(item => selectedFiscalYears.includes(item.fiscal_year));
    }

    if (selectedEntityNames?.length) {
      tableData = tableData.filter(item => selectedEntityNames.includes(item.entity_name));
    }
    if (selectedVendorNames?.length) {
      tableData = tableData.filter(item => selectedVendorNames.includes(item.vendor_name));
    }

    // Sorted variables for filter creation
    const sortedFiscalYears = [...new Set(masterData.map((item: any) => Number(item.fiscal_year)))]
      .sort((a: any, b: any) => b - a) as number[];
    setAllFiscalYears(sortedFiscalYears);

    const sortedEntityNames = [...new Set(masterData.map((item: any) => item.entity_name))]
      .sort((a: any, b: any) => a.localeCompare(b)) as string[];
    setAllEntityNames(sortedEntityNames);

    const sortedVendorNames = [...new Set(masterData.map((item: any) => item.vendor_name))]
      .sort((a: any, b: any) => a.localeCompare(b)) as string[];
    setAllVendorNames(sortedVendorNames);

    setFilteredData(tableData);

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Destroy existing table instance
    if (tabulatorRef.current) {
      tabulatorRef.current.destroy();
    }

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: tableData,
      minHeight: "450px",
      maxHeight: "100%",
      layout: isMobile ? "fitDataFill" : "fitColumns",
      pagination: "local",
      paginationSize: 10,
      placeholder: "No Data Available",
      headerSort: true,
      initialSort: [
        {
          column: "net_amount",
          dir: "desc",
        }
      ],
      columns: [
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-calendar'></i> Year";
            return el;
          },
          title: "Fiscal Year",
          field: "fiscal_year",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-landmark'></i> Entity";
            return el;
          },
          title: "Entity",
          field: "entity_name", 
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-people-carry'></i> Vendor";
            return el;
          },
          title: "Vendor",
          field: "vendor_name", 
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-dollar-sign'></i> Total";
            return el;
          },
          title: "Total",
          field: "net_amount",
          sorter: "number",
          formatter: "money",
          formatterParams: { symbol: "$" }
        },
      ]
    });

    // Added to help with initial load/draw issues
    requestAnimationFrame(() => {
      setTimeout(() => {
      tabulatorRef.current?.redraw(true);
    }, 50);
    });

    return () => {
      if (tabulatorRef.current) {
         tabulatorRef.current.destroy();
         tabulatorRef.current = null;
      };
    };
  }, [masterData, selectedFiscalYears, selectedEntityNames, selectedVendorNames]);

  // Updated Render D3 Chart
  useEffect(() => {
    if (!masterData || masterData.length === 0) return;

    const svg = d3.select(chartRef.current);
    const container = chartRef.current?.parentElement;

    // Add Filters
    let tableData = [...masterData];

    if (selectedFiscalYears?.length) {
      tableData = tableData.filter(item => selectedFiscalYears.includes(item.fiscal_year));
    }

    if (selectedEntityNames?.length) {
      tableData = tableData.filter(item => selectedEntityNames.includes(item.entity_name));
    }

    if (selectedVendorNames?.length) {
      tableData = tableData.filter(item => selectedVendorNames.includes(item.vendor_name));
    }
    
    const renderChart = () => {
      if (!container || !chartRef.current) return;

      const containerWidth = container.offsetWidth;
      const margin = { top: 30, right: 30, bottom: 75, left: 80 };
      const width = containerWidth - margin.left - margin.right;
      const height = 400 - margin.top - margin.bottom;

      // Clear previous render
      svg.selectAll("*").remove();
      d3.select(container).selectAll(".tooltip").remove();

      svg
        .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
        .attr("preserveAspectRatio", "xMidYMid meet");

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Aggregate data by fiscal year for Vendor Search
      const dataByYear: any[] = [];
      tableData.reduce((res: Record<number, any>, d: any) => {
        if (!res[d.fiscal_year]) {
          res[d.fiscal_year] = { fiscal_year: d.fiscal_year, net_amount: 0 };
          dataByYear.push(res[d.fiscal_year]);
        }
        res[d.fiscal_year].net_amount += d.net_amount;
        return res;
      }, {});

      // Sort data by fiscal year
      dataByYear.sort((a, b) => a.fiscal_year - b.fiscal_year);
      
      const keys: string[] = ["net_amount"];

      const x = d3.scaleBand<string>()
        .domain(dataByYear.map(d => d.fiscal_year.toString()))
        .range([0, width])
        .padding(0.2);

      const y = d3.scaleLinear()
        .domain([0, d3.max(dataByYear, d => d.net_amount) || 0])
        .nice()
        .range([height, 0]);

      const z = d3.scaleOrdinal<string>()
        .domain(keys)
        .range(["#ffc63a"]);

      // Tooltip
      const tooltip = d3.select(container)
        .append("div")
        .attr("class", "tooltip")
        .style("position", "absolute")
        .style("visibility", "hidden")
        .style("background", "white")
        .style("border", "1px solid gray")
        .style("padding", "5px")
        .style("border-radius", "4px")
        .style("pointer-events", "none")
        .style("font-size", "12px")
        .style("z-index", "1000");

      // Legend
      const legend = svg.append("g")
        .attr("class", "legend")
        .attr("transform", `translate(${margin.left + 5},${margin.top})`);

      legend.selectAll("rect")
        .data(keys)
        .enter()
        .append("rect")
        .attr("x", (d, i) => i * 100)
        .attr("y", 0)
        .attr("width", 15)
        .attr("height", 15)
        .attr("fill", d => z(d) as string);

      legend.selectAll("text.label")
        .data(keys)
        .enter()
        .append("text")
        .attr("class", "label")
        .attr("x", (d, i) => i * 100 + 20)
        .attr("y", 12)
        .text("Net Amount")
        .style("font-size", "12px")
        .attr("fill", "#000");

      // Stack generator - using any to avoid YearData interface mismatch
      const stackGenerator = d3.stack<any>().keys(keys);
      const stackSeries = stackGenerator(dataByYear);

      g.selectAll("g.layer")
        .data(stackSeries)
        .join("g")
        .attr("class", "layer")
        .attr("fill", d => z(d.key) as string)
        .selectAll("rect")
        .data(d => d)
        .join("rect")
        .attr("x", (d: any) => x(d.data.fiscal_year.toString()) ?? 0)
        .attr("y", (d: any) => y(d[1]))
        .attr("height", (d: any) => y(d[0]) - y(d[1]))
        .attr("width", x.bandwidth())
        .on("mouseover", (event: any, d: any) => {
          tooltip
            .style("visibility", "visible")
            .style("opacity", "1")
            .html(`
              <strong>${d.data.fiscal_year}</strong><br/>
              Total: $${(d[1] - d[0]).toLocaleString()}
            `);
        })
        .on("mousemove", (event: any) => {
          const [posX, posY] = d3.pointer(event, container);
          tooltip.style("top", `${posY - 30}px`).style("left", `${posX + 10}px`);
        })
        .on("mouseout", () => {
          tooltip.style("visibility", "hidden");
        });

      // Axes
      g.append("g")
        .call(d3.axisLeft(y).tickFormat(d3.format("$,.0f")));

      g.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(x));
    };

    renderChart();

    const resizeObserver = new ResizeObserver(() => renderChart());
    if (chartRef.current?.parentElement) resizeObserver.observe(chartRef.current.parentElement);

    return () => resizeObserver.disconnect();

  }, [masterData, selectedFiscalYears, selectedEntityNames, selectedVendorNames]); 

  // Setting memoized options for MultiselectAsync dropdown forms
  const fiscalYearOptions = useMemo(() => 
    (allFiscalYears || []).map(year => ({ value: year.toString(), label: year.toString() })), 
  [allFiscalYears]);

  const entityNameOptions = useMemo(() => 
    (allEntityNames || []).map(name => ({ value: name, label: name })), 
  [allEntityNames]);

  const vendorNameOptions = useMemo(() => 
    (allVendorNames || []).map(vendor_name => ({ value: vendor_name, label: vendor_name })), 
  [allVendorNames]);

  return (
    <>
      <HeaderComponent />
      <FeatureNav/>
      <BreadCrumbs/>
      <Container fluid as="main">
        <Row className="mb-4 px-4">
          <Col xs={12} className="px-0">
            <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body style={{ justifyItems: "center", textAlign: "center" }}>
              <div className="tu_tabHeader">
                <div className="tu-tabHeader__titleRow">
                  <h1 className="tu-tabHeader__title">
                  Vendor Payments Search</h1>
                    <button
                    type="button"
                    className="tu-tabHeader__infoBtn"
                    onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                    aria-label="Most Common Jobs info"
                  >
                    <i className="fas fa-info"/>
                    </button>
                  </div>
                </div>
                <Collapse in={showHeaderInfo}>
                  <div>
                  <p className="lead mt-2 text-center">
                    To search for vendor payments, enter a name and click "Submit". The name submitted will return partial matches on a reported vendor name. Different government entities may use different names for what seems to be the same vendor. "Office Depot/Office Max" may include the city name, the store number, or just one of the two names.
                  </p>
                  <p className="lead mt-2 text-center">
                    After clicking submit, use the filters to refine the search by fiscal year, entity, and vendor name. Both the Plot and Table will automatically update as filters are added/removed.
                  </p>
                  </div>
                </Collapse>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Loader */}
        {loading ? <Loader /> : <></>}

        {/* Search */}
        <Row className="px-4">
          <Col xs={12} className="px-0">
            <Card>
              <Card.Header className="text-white" style={{ backgroundColor: "var(--tu-teal)"}}>
                <Card.Title className="text-white" as="h2" style={{ fontSize: "1.5rem", textTransform: "none" }}>Search</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={(event: any) => {
                  event.preventDefault();
                  const val = event.target[0].value;
                  if (val) {
                    setLoading(true);
                    setSelectedVendorSearch(val);
                  }
                }}>
                  <Row>
                    <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 mt-2">
                      <Form.Control 
                        type="text" 
                        placeholder="Enter vendor name" 
                      />
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-lg-2 col-xl-2 mt-2 submit-button-container">
                      <Button 
                        variant="secondary"
                        type="submit"
                      >
                        <i className="fas fa-paper-plane"></i>{" "}Submit
                      </Button>
                    </div>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filter Section */}
        <Row className="px-4 mb-2">
          <Col xs={12} className="h-100 px-0">
            <Card className="mt-4">
              <Card.Header className="bg-secondary text-white">
                <Card.Title className="text-white" as="h2" style={{ fontSize: "1.5rem", textTransform: "none" }}>Filter</Card.Title>
              </Card.Header>

              <Card.Body className="px-0 mx-0 pt-0">
                <Row id="container-filter" className="px-4 py-2">
                  {/* Fiscal Year Filter */}
                  <Col xs={12} sm={12} md={6} lg={4} xl={4} className="py-2">
                    <h5>
                      <i className="fas fa-calendar"/>
                      {" "}Filter Year
                      <MultiSelectAsync
                        options={fiscalYearOptions? fiscalYearOptions : []}
                        defaultOptions={fiscalYearOptions ? fiscalYearOptions : []}
                        value={
                          selectedFiscalYears?.map(year => ({
                            value: year.toString(),
                            label: year.toString(),
                          })) || []
                        }
                        isSearchable={true}
                        onChange={(selected) => {
                          const values = selected ? selected.map(s => Number(s.value)) : [];
                          setSelectedFiscalYears(values);
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return fiscalYearOptions;
                          return fiscalYearOptions?.filter(o =>
                            o.label.toString().toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
                  </Col>

                  {/* Entity Filter */}
                  <Col xs={12} sm={12} md={6} lg={4} xl={4} className="py-2">
                    <h5>
                      <i className="fas fa-landmark"/>
                      {" "}Filter Entity
                      <MultiSelectAsync
                        options={entityNameOptions} 
                        defaultOptions={entityNameOptions}
                        value={selectedEntityNames ? selectedEntityNames?.map(name => ({
                          value: name,
                          label: name,
                        })) : []}
                        isSearchable={true}
                        onChange={(selected) => {
                          const entityNameOptions = selected ? selected.map(s => s.value) : [];
                          setSelectedEntityNames(entityNameOptions);
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return entityNameOptions;
                          return entityNameOptions.filter(o => 
                            o.label.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
                  </Col>

                  {/* Vendor Filter */}
                  <Col xs={12} sm={12} md={6} lg={4} xl={4} className="py-2">
                    <h5>
                      <i className="fas fa-people-carry"/>
                      {" "}Filter Vendor
                      <MultiSelectAsync
                        options={vendorNameOptions}
                        defaultOptions={vendorNameOptions}
                        value={selectedVendorNames ? selectedVendorNames?.map(name => ({
                          value: name,
                          label: name,
                        })) : []}
                        isSearchable={true}
                        onChange={(selected) => {
                          const vendorNameOptions = selected ? selected.map(s => s.value) : [];
                          setSelectedVendorNames(vendorNameOptions);
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return vendorNameOptions;
                          return vendorNameOptions.filter(o =>
                            o.label.toString().toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabulator Table */}
        <Row className="px-4 mb-3">
          <Col xs={12} className="px-0">
            <Card className="mt-4 shadow-sm">
              <Card.Header className="bg-secondary text-white">
                <Card.Title className="text-white" as="h2" style={{ fontSize: "1.5rem", textTransform: "none" }}>Data</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="mb-2 d-flex gap-2">
                  <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                </div>
                <div style={{ width: "100%", position: "relative"}}>
                  {/* Loading */}
                  {loading && (
                    <div className="text-center py-5">
                      <div className="text-primary" role="status"></div>
                      {/* Loader */}
                      {loading ? <Card.Text><Loader /></Card.Text> : <></>}
                    </div>
                  )}

                  {/* No Data Available */}
                  {!loading && (!filteredData || filteredData.length === 0) && (
                    <Card.Text className="text-center py-5">No Data available - please search for a vendor</Card.Text>
                  )}

                  <div 
                    ref={tableRef} 
                    className="table-light"
                    style={{ 
                      minHeight: 400,
                      opacity: (!loading && (filteredData?.length ?? 0) > 0) ? 1 : 0,
                      height: (!loading && (filteredData?.length ?? 0) > 0) ? 'auto' : '0px',
                      overflow: 'hidden'
                    }} 
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* D3 Chart */}
        <Row className="px-4 mb-3">
          <Col xs={12} className="px-0">
            <Card className="mt-4 shadow-sm">
              <Card.Header className="bg-secondary text-white">
                <h3 className="text-white">Chart</h3>
              </Card.Header>
              <Card.Body>
                <div id="chart-container" ref={containerRef} style={{ width: "100%" }}>
                  {(!masterData || masterData?.length < 1) && (
                      <p className="text-lead text-center py-5">
                        No data - please search for a vendor
                      </p>
                    )}

                  <div className={(masterData?.length ?? 0) > 1 ? "d-block" : "d-none"}>
                    <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                      <div
                        className="btn btn-secondary"
                        id="btn-chart-download"
                        onClick={() => chartRef.current && handleD3ChartDownload(chartRef.current)}
                        style={{ margin: 0 }}
                      >
                        Download
                      </div>

                      <div
                        className="btn btn-secondary"
                        id="btn-chart-fullscreen"
                        data-toggle="modal"
                        data-target="#fullscreen-modal"
                        style={{ margin: 0 }}
                      >
                        <i className="fas fa-expand-arrows-alt"></i>
                      </div>
                    </div>
                  </div>

                  {/* Loading */}
                  {loading && (
                    <div className="text-center py-5">
                      <div className="text-primary" role="status"></div>
                      {/* Loader */}
                      {loading ? <Card.Text><Loader /></Card.Text> : <></>}
                    </div>
                  )}

                  {/* Chart SVG */}
                  <svg ref={chartRef} style={{ width: "100%", height: "400px" }} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <FooterSection />
    </>
  )
}

export default EntityVendorPaymentSearch;